import { MaxInputLimitation, Value } from '@ui/features/Trade/types';
import { Button } from '@ui/lib/park/ui/button';
import { Text } from '@ui/lib/park/ui/text';
import { ChainId } from '@ui/types/chain';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { TFunction } from 'next-i18next';
import { HStack } from 'styled-system/jsx';
import { Nullable } from 'vitest';

import {
  formatPayLabel,
  MaxCollateralAmountError,
  maxInputValidationCheck,
} from './validateCollateralAmount';

type DepositCollateralError =
  | 'balance_insufficient'
  | 'below_minimum_input_0'
  | 'below_degen_mode_min_leverage'
  | 'below_normal_mode_min_leverage'
  | MaxCollateralAmountError;

export const validateDepositCollateralAmount = ({
  input,
  balance,
  minLeverage,
  nextLeverage,
  maxInput,
  isLong,
  isDegen,
}: {
  input: Value;
  balance?: Nullable<BigNumber>;
  minLeverage: Nullable<number>;
  nextLeverage: Nullable<BigNumber>;
  maxInput: MaxInputLimitation;
  isLong: boolean;
  isDegen: boolean;
}): {
  error?: DepositCollateralError;
} => {
  if (isNil(input.usd) || isNil(input.token) || isNil(balance)) {
    return {};
  }

  if (input.token.gt(balance)) {
    return {
      error: 'balance_insufficient',
    };
  }

  if (input.token.lte(0) || input.usd.lte(0)) {
    return {
      error: 'below_minimum_input_0',
    };
  }

  if (!isNil(minLeverage) && !isNil(nextLeverage)) {
    if (nextLeverage.lte(minLeverage)) {
      if (isDegen) {
        return {
          error: 'below_degen_mode_min_leverage',
        };
      } else {
        return {
          error: 'below_normal_mode_min_leverage',
        };
      }
    }
  }

  const maxInputValidationCheckResult = maxInputValidationCheck(
    input.usd,
    maxInput,
    isLong,
  );

  if (maxInputValidationCheckResult?.error) {
    return maxInputValidationCheckResult;
  }

  return {};
};

export const getDepositCollateralAmountErrorMessage = ({
  t,
  error,
  isInputToken,
  symbol,
  maxInput,
  leverage,
  minLeverage,
  chainId,
}: {
  t: TFunction;
  error?: DepositCollateralError;
  isInputToken: boolean;
  symbol: Nullable<string>;
  maxInput: MaxInputLimitation;
  leverage: Nullable<number>;
  minLeverage: number;
  chainId: ChainId;
}) => {
  if (!error) {
    return '';
  }

  switch (error) {
    case 'balance_insufficient':
      return (
        <HStack gap={1}>
          <Text>{t('common.Balance insufficient.')}</Text>
          {/* <Button variant="link">{t('common.Replenish now')}</Button> */}
        </HStack>
      );
    case 'below_degen_mode_min_leverage':
      return t('common.Below degen mode min leverage {minLeverage}x', {
        minLeverage,
      });
    case 'below_normal_mode_min_leverage':
      return t('common.Below normal mode min leverage {minLeverage}x', {
        minLeverage,
      });
    case 'below_minimum_input_0':
      return t('common.Collateral amount must be greater than 0');
    case 'must_lt_leverageMarginNotionalUsd':
      const { leverageMarginNotionalMaxLimit } = maxInput;

      return t('common.Max notional USD reached', {
        collateralInUsdOrAmount: formatPayLabel(
          isInputToken,
          symbol,
          leverageMarginNotionalMaxLimit,
        ),
        leverage: leverage?.toFixed(1),
      });

    case 'must_lt_maxInputMaxLongOILimit':
      const { maxInputMaxLongOILimit } = maxInput;

      return t(
        'common.OI reaching limit {collateralInUsdOrAmount} {leverage}x',
        {
          collateralInUsdOrAmount: formatPayLabel(
            isInputToken,
            symbol,
            maxInputMaxLongOILimit,
          ),
          leverage: leverage?.toFixed(1),
        },
      );

    case 'must_lt_maxInputMaxShortOILimit':
      const { maxInputMaxShortOILimit } = maxInput;

      return t(
        'common.OI reaching limit {collateralInUsdOrAmount} {leverage}x',
        {
          collateralInUsdOrAmount: formatPayLabel(
            isInputToken,
            symbol,
            maxInputMaxShortOILimit,
          ),
          leverage: leverage?.toFixed(1),
        },
      );

    default:
      throw new Error('should not have default case');
  }
};
